import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { UserProfileComponent } from '@molecules/user-profile/user-profile.component';
import { NavLinkComponent } from '@molecules/nav-link/nav-link.component';
import { AuthService } from '@services/auth.service';
import { APP_ROUTES } from '@routes/app-routes.constants';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [UserProfileComponent, NavLinkComponent, RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly isLoggedIn = toSignal(this.authService.isLoggedIn$, { initialValue: false });
  protected readonly isPlatformAdmin = toSignal(this.authService.isPlatformAdmin$, {
    initialValue: false,
  });
  protected readonly canManageAms = toSignal(this.authService.canManageAms$, {
    initialValue: false,
  });
  protected readonly canAccessAms = toSignal(this.authService.canAccessAms$, {
    initialValue: false,
  });
  protected readonly isTeamTrackingManager = toSignal(this.authService.isTeamTrackingManager$, {
    initialValue: false,
  });

  protected readonly clientsPath = APP_ROUTES.clients.root;
  protected readonly homePath = APP_ROUTES.home;
  protected readonly amsPath = APP_ROUTES.ams.root;
  protected readonly teamTrackingPath = APP_ROUTES.teamTracking.root;
  protected readonly settingsPath = APP_ROUTES.admin.settings;

  private readonly userProfile = toSignal(this.authService.userProfile$);
  protected readonly userName = computed(() =>
    this.authService.getManualToken() ? 'Manual Token' : (this.userProfile()?.name ?? '')
  );
  protected readonly userPhotoUrl = computed(() =>
    this.authService.getManualToken() ? '' : (this.userProfile()?.photoUrl ?? '')
  );

  protected readonly menuOpen = signal(false);

  constructor() {
    // Fecha o menu em qualquer navegação concluída (botão voltar/avançar do browser incluído)
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.menuOpen.set(false));
  }

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected logout(): void {
    this.authService.logout();
  }
}
